import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@shared/store/authStore';
import { format, addDays, parseISO, differenceInHours } from 'date-fns';
import { ru } from 'date-fns/locale';
import { notifyAdminAboutCancellation } from '../utils/bookingNotifications';
import { getRestaurantOrders, type RestaurantOrder } from '../utils/restaurantOrders';
import { QRCodeSVG } from 'qrcode.react';

interface ClientBooking {
  id: string;
  serviceName: string;
  employeeName?: string;
  date: string;
  time: string;
  status: 'confirmed' | 'pending' | 'completed' | 'cancelled';
  price: number;
  location?: string;
  isGroupBooking?: boolean;
  isCourtBooking?: boolean;
}

interface ClientCard {
  id: string;
  number: string;
  type: string;
  balance: number;
  expiryDate: string;
  status: 'active' | 'blocked' | 'expired';
}

interface Employee {
  Id: number;
  first_name: string;
  last_name: string;
  middle_name: string;
  spec_name: string;
}

function getDaysWord(days: number): string {
  const lastTwo = days % 100;
  const lastOne = days % 10;
  if (lastTwo >= 11 && lastTwo <= 19) return 'дней';
  if (lastOne === 1) return 'день';
  if (lastOne >= 2 && lastOne <= 4) return 'дня';
  return 'дней';
}

export function ProfilePage() {
  const { user, apiClient, updateUser, logout } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [clientInfo, setClientInfo] = useState<any>(null);
  const [clientBookings, setClientBookings] = useState<ClientBooking[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [devices, setDevices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [activeCard, setActiveCard] = useState<any>(null);
  const [restaurantOrders, setRestaurantOrders] = useState<RestaurantOrder[]>([]);
  const [effectiveBonusInfo, setEffectiveBonusInfo] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    middleName: user?.middleName || '',
    phone: user?.phone || '',
    email: user?.email || '',
    birthday: user?.birthday || '',
    city: user?.city || '',
    address: user?.address || '',
  });

  // Club cards based on real client data
  const clientCards: ClientCard[] = clientInfo?.card_number ? [
    {
      id: '1',
      number: clientInfo.card_number,
      type: 'Клубная карта',
      balance: 5500, // Mock balance until API provides it
      expiryDate: '2025-12-31',
      status: 'active'
    }
  ] : [];

  useEffect(() => {
    loadClientData();
    // Загружаем историю заказов из ресторана
    setRestaurantOrders(getRestaurantOrders());
  }, [user?.phone, apiClient]);

  // Group consecutive court bookings into single entries with time ranges
  const groupConsecutiveCourtBookings = (bookings: any[]): any[] => {
    // Separate court bookings from other bookings
    const courtBookings = bookings.filter(b => b.is_court || b.DeviceId);
    const otherBookings = bookings.filter(b => !b.is_court && !b.DeviceId);

    if (courtBookings.length === 0) {
      return bookings;
    }

    // Sort court bookings by time
    const sortedCourts = courtBookings.sort((a, b) => {
      const timeA = new Date(a.time_from.replace(' ', 'T')).getTime();
      const timeB = new Date(b.time_from.replace(' ', 'T')).getTime();
      return timeA - timeB;
    });

    const grouped: any[] = [];
    let currentGroup: any[] = [];

    for (let i = 0; i < sortedCourts.length; i++) {
      const booking = sortedCourts[i];

      if (currentGroup.length === 0) {
        currentGroup.push(booking);
        continue;
      }

      const lastBooking = currentGroup[currentGroup.length - 1];

      // Check if this booking is consecutive (same court, same day, time_to of last = time_from of current)
      const isSameCourt = booking.DeviceId === lastBooking.DeviceId;
      const lastEndTime = new Date(lastBooking.time_to.replace(' ', 'T'));
      const currentStartTime = new Date(booking.time_from.replace(' ', 'T'));
      const timeDiff = Math.abs(currentStartTime.getTime() - lastEndTime.getTime()) / (1000 * 60); // in minutes

      if (isSameCourt && timeDiff <= 1) {
        // Consecutive booking - add to current group
        currentGroup.push(booking);
      } else {
        // Not consecutive - merge current group and start new one
        grouped.push(mergeCourtBookings(currentGroup));
        currentGroup = [booking];
      }
    }

    // Don't forget the last group
    if (currentGroup.length > 0) {
      grouped.push(mergeCourtBookings(currentGroup));
    }

    return [...otherBookings, ...grouped];
  };

  // Merge multiple court bookings into one with time range
  const mergeCourtBookings = (bookings: any[]): any => {
    if (bookings.length === 1) {
      return bookings[0];
    }

    // Use first booking as base
    const merged = { ...bookings[0] };

    // Update time_to to be the end time of the last booking
    const lastBooking = bookings[bookings.length - 1];
    merged.time_to = lastBooking.time_to;

    // Combine IDs (for deletion purposes)
    merged.Id = bookings.map(b => b.Id).join(',');

    return merged;
  };

  const loadClientData = async () => {
    if (!user?.phone || !apiClient) {
      return;
    }

    setIsLoading(true);
    try {
      // Load employees list for trainer names
      const employeesData = await apiClient.getEmployees();
      setEmployees(employeesData);

      // Загружаем список устройств/кабинетов
      const devicesData = await apiClient.getDevices();
      setDevices(devicesData);

      // First find client to get ClientId
      const clientResult = await apiClient.checkPhoneExists(user.phone);
      if (clientResult.exists && clientResult.clientInfo?.ClientId) {
        // Load full client info using get_client method
        const fullInfo = await apiClient.getClient(clientResult.clientInfo.ClientId);
        if (fullInfo) {
          setClientInfo(fullInfo);
          // Update form data with real client info
          setFormData({
            firstName: fullInfo.first_name || '',
            lastName: fullInfo.last_name || '',
            middleName: fullInfo.middle_name || '',
            phone: fullInfo.phone || user.phone,
            email: fullInfo.email || '',
            birthday: fullInfo.day && fullInfo.month && fullInfo.year
              ? `${fullInfo.year}-${fullInfo.month.toString().padStart(2, '0')}-${fullInfo.day.toString().padStart(2, '0')}`
              : '',
            city: fullInfo.city || '',
            address: fullInfo.address || '',
          });

          // Load effective bonus balance with expiry info
          let effectiveBalance = fullInfo.bonus_balance || 0;
          try {
            const bonusResp = await fetch(`/api/bonuses/${clientResult.clientInfo.ClientId}/effective-balance`);
            const bonusData = await bonusResp.json();
            if (bonusData.success && bonusData.balance) {
              setEffectiveBonusInfo(bonusData.balance);
              effectiveBalance = bonusData.balance.effective;
            }
          } catch (e) {
            // Fallback to raw balance
          }

          // Update user in authStore with FIO data and effective bonus balance
          const updateData = {
            firstName: fullInfo.first_name || '',
            lastName: fullInfo.last_name || '',
            middleName: fullInfo.middle_name || '',
            bonusBalance: effectiveBalance,
          };
          updateUser(updateData);

          // Load client cards to get active card name
          const cards = await apiClient.getClientCards(clientResult.clientInfo.ClientId);
          const activeCardData = cards.find((card: any) => card.card_status?.id === 1);
          if (activeCardData) {
            setActiveCard(activeCardData);
          }
        }
      }

      // Load client bookings (last 30 days and next 30 days)
      const dateFrom = format(addDays(new Date(), -30), 'yyyy-MM-dd');
      const dateTo = format(addDays(new Date(), 30), 'yyyy-MM-dd');

      const bookings = await apiClient.getClientBookings(user.phone, dateFrom, dateTo);

      // Group consecutive court bookings
      const groupedBookings = groupConsecutiveCourtBookings(bookings);

      // Transform API bookings to our format - filter out invalid bookings
      const transformedBookings: ClientBooking[] = groupedBookings
        .filter((booking: any) => {
          // Skip bookings without time_from - they're invalid
          if (!booking.time_from) {
            console.warn('⚠️ Skipping booking without time_from:', booking);
            return false;
          }
          return true;
        })
        .map((booking: any, index: number) => {
          // Parse date from time_from (format: "YYYY-MM-DD HH:mm")
          let bookingDate = '';
          let bookingTime = '';

          try {
            // Parse "2023-09-21 13:00" format
            const dateObj = new Date(booking.time_from.replace(' ', 'T'));
            if (!isNaN(dateObj.getTime())) {
              bookingDate = format(dateObj, 'yyyy-MM-dd');

              // For court bookings, show time range
              if ((booking.is_court || booking.DeviceId) && booking.time_to) {
                const endTimeObj = new Date(booking.time_to.replace(' ', 'T'));
                const startTime = format(dateObj, 'HH:mm');
                const endTime = format(endTimeObj, 'HH:mm');
                bookingTime = `${startTime}-${endTime}`;
              } else {
                bookingTime = format(dateObj, 'HH:mm');
              }
            } else {
              throw new Error('Invalid date');
            }
          } catch (e) {
            console.error('Error parsing time_from:', booking.time_from, e);
            // Use current date as last resort
            bookingDate = format(new Date(), 'yyyy-MM-dd');
            bookingTime = '00:00';
          }

        // Get service name from Services array or comment
        let serviceName = 'Услуга';
        let servicePrice = 0;
        if (booking.Services && booking.Services.length > 0) {
          const firstService = booking.Services[0];
          serviceName = firstService.service_name || firstService.name || serviceName;
          servicePrice = firstService.price || firstService.sum || 0;
        } else if (booking.comment) {
          // If Services is empty, extract service name from comment
          // Comment format: "Beauty услуга - Массажист"
          serviceName = booking.comment;
        }

        // Determine booking type
        const isGroupBooking = booking.Services && booking.Services.length > 0 && booking.Services[0].is_group === 1;
        const isPersonalTraining = booking.comment && booking.comment.includes('Персональная тренировка');
        const isCourtBooking = !isGroupBooking && !isPersonalTraining && (
          (booking.DeviceId) || // Has court device ID
          (booking.comment && (
            booking.comment.toLowerCase().includes('корт') ||
            booking.comment.toLowerCase().includes('теннис') ||
            booking.comment.toLowerCase().includes('падел')
          )) ||
          (serviceName && (
            serviceName.toLowerCase().includes('корт') ||
            serviceName.toLowerCase().includes('теннис') ||
            serviceName.toLowerCase().includes('падел')
          )) ||
          // Court bookings have GUID format with dashes AND no Services
          (booking.Id && booking.Id.includes('-') && (!booking.Services || booking.Services.length === 0))
        );

        // Get employee name from employees list
        let employeeName = '';
        if (booking.EmpId) {
          // Check if this is a personal training or group lesson booking
          const isPersonalTraining = booking.comment && booking.comment.includes('Персональная тренировка');

          let prefix = 'Специалист';
          if (isPersonalTraining || isGroupBooking) {
            prefix = 'Тренер';
          }

          // Find employee in employeesData (not employees state!)
          const employee = employeesData.find(emp => emp.Id === booking.EmpId);
          if (employee) {
            employeeName = `${prefix}: ${employee.first_name} ${employee.last_name}`;
          } else {
            employeeName = `${prefix} #${booking.EmpId}`;
          }
        }

        // Get device/location name
        let location = '';
        if (booking.DeviceId) {
          // Ищем устройство в списке
          const device = devicesData.find(d => d.id === booking.DeviceId);
          if (device) {
            // Если это кабинет (18, 22) - показываем название
            if (booking.DeviceId === 18 || booking.DeviceId === 22) {
              location = `📍${device.name}`;
            } else {
              // Для кортов НЕ показываем номер корта, только тип из comment или court_name
              if (booking.comment && (booking.comment.includes('корт') || booking.comment.includes('Корт'))) {
                // Extract court type from comment (e.g., "Теннисные корты", "Падел корты")
                const courtTypeMatch = booking.comment.match(/(Теннисные корты|Падел корты|Корт)/i);
                location = courtTypeMatch ? `📍${courtTypeMatch[1]}` : `📍${device.name}`;
              } else if (booking.court_name) {
                location = `📍${booking.court_name}`;
              } else {
                location = `📍${device.name}`;
              }
            }
          } else {
            location = '';
          }
        } else if (booking.comment && booking.comment.includes('Beauty')) {
          location = 'SPA зона';
        }

        return {
          id: booking.Id || booking.booking_id || index.toString(),
          serviceName: serviceName,
          employeeName: employeeName,
          date: bookingDate,
          time: bookingTime,
          status: 'confirmed', // API doesn't return status, assume confirmed
          price: servicePrice,
          location: location,
          isGroupBooking: isGroupBooking,
          isCourtBooking: isCourtBooking
        };
      });

      setClientBookings(transformedBookings);
    } catch (error) {
      console.error('Error loading profile data:', error);
      setClientBookings([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    updateUser(formData);
    setIsEditing(false);
  };

  const handleCancelBooking = async (bookingId: string, date: string, time: string, serviceName: string, employeeName?: string, isCourtBooking: boolean = false, isGroupBooking: boolean = false) => {
    if (!apiClient || !user) return;

    // Extract start time from time range (e.g., "06:30-07:30" -> "06:30")
    const startTime = time.includes('-') ? time.split('-')[0] : time;
    const bookingDateTime = parseISO(`${date}T${startTime}`);
    const now = new Date();
    const hoursUntilBooking = differenceInHours(bookingDateTime, now);

    if (hoursUntilBooking < 4) {
      alert('Отмена записи возможна не позднее чем за 4 часа до начала');
      return;
    }

    if (!confirm('Вы уверены, что хотите отменить запись?')) {
      return;
    }

    try {
      setIsLoading(true);

      // Cancel booking via API using appropriate method
      if (isGroupBooking) {
        // Get client info to get ClientId
        const clientInfo = await apiClient.getClientInfo(user.phone);
        if (!clientInfo || !clientInfo.ClientId) {
          throw new Error('Не удалось получить информацию о клиенте');
        }
        await apiClient.unregisterClientFromTraining(clientInfo.ClientId, bookingId);
      } else if (isCourtBooking) {
        // If bookingId contains multiple IDs (merged bookings), delete each separately
        const bookingIds = bookingId.includes(',') ? bookingId.split(',') : [bookingId];
        for (const id of bookingIds) {
          await apiClient.deleteCourtBooking(id.trim());
        }
      } else {
        await apiClient.deleteBooking(bookingId);
      }

      // Отправляем уведомление админу в Telegram
      await notifyAdminAboutCancellation({
        clientId: user.id,
        clientName: `${user.firstName} ${user.lastName}`,
        date: format(parseISO(date), 'dd.MM.yyyy', { locale: ru }),
        time,
        serviceName,
        bookingId
      });

      // Remove booking from local state immediately for smooth UX
      setClientBookings(prev => prev.filter(b => b.id !== bookingId));

      // Silently reload data in background without blocking UI
      loadClientData().catch(err => {
        console.error('Error reloading profile after cancel:', err);
        // Don't show error to user, data already updated locally
      });

    } catch (error) {
      console.error('❌ Error canceling booking:', error);
      // Don't show error alert - booking already removed from local state
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleLogout = () => {
    logout();
    setShowLogoutConfirm(false);
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка профиля...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Personal Account Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-gray-900">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Личный кабинет</h1>
          <div className="flex items-center space-x-1">
            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
            <span className="text-sm">Активный</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* User Info */}
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-xl font-bold">
              {user.firstName?.[0] || 'У'}{user.lastName?.[0] || 'К'}
            </div>
            <div>
              <div className="text-lg font-semibold">
                {user.lastName || ''} {user.firstName || ''} {user.middleName || ''}
              </div>
              <div className="text-emerald-100">{user.phone}</div>
              <div className="text-emerald-200 text-sm">ID: {clientInfo?.ClientId || user.id}</div>
            </div>
          </div>
          {/* Quick Stats */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-emerald-100">Клубная карта:</span>
              <span className="font-semibold">{activeCard?.name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-emerald-100">Баланс:</span>
              <span className="font-semibold">{clientInfo?.money || '0'}₽</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-emerald-100">Бонусы:</span>
              <span className="font-semibold">{effectiveBonusInfo?.effective ?? (clientInfo?.bonus_balance || '0')}₽</span>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Личная информация</h2>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between py-3 border-b border-gray-100">
            <span className="text-gray-700 font-medium">ФИО:</span>
            <span className="font-semibold text-gray-900">
              {user.lastName} {user.firstName} {user.middleName}
            </span>
          </div>
          <div className="flex justify-between py-3 border-b border-gray-100">
            <span className="text-gray-700 font-medium">Телефон:</span>
            <span className="font-semibold text-blue-600">{user.phone}</span>
          </div>
          <div className="flex justify-between py-3 border-b border-gray-100">
            <span className="text-gray-700 font-medium">Клубная карта:</span>
            <span className="font-semibold text-orange-600">{activeCard?.name}</span>
          </div>
          <div className="flex justify-between py-3 border-b border-gray-100">
            <span className="text-gray-700 font-medium">Баланс:</span>
            <span className="font-semibold text-emerald-600 text-lg">{clientInfo?.money || '0'}₽</span>
          </div>
          <div className="flex justify-between py-3 border-b border-gray-100">
            <span className="text-gray-700 font-medium">Бонусы:</span>
            <span className="font-semibold text-purple-600 text-lg">{effectiveBonusInfo?.effective ?? (clientInfo?.bonus_balance || '0')}₽</span>
          </div>
          {effectiveBonusInfo?.nextExpiry && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-amber-800 mb-1">Бонусы скоро сгорят</p>
              {effectiveBonusInfo.expiringSoon.map((item: any, idx: number) => (
                <p key={idx} className="text-sm text-amber-700">
                  {item.amount}₽ — через {item.daysLeft} {getDaysWord(item.daysLeft)}
                </p>
              ))}
              {effectiveBonusInfo.expired > 0 && (
                <p className="text-xs text-amber-600 mt-2">Уже сгорело: {effectiveBonusInfo.expired}₽</p>
              )}
            </div>
          )}
          <div className="flex justify-between py-3">
            <span className="text-gray-700 font-medium">ID клиента:</span>
            <span className="font-semibold font-mono text-sm text-gray-800">{clientInfo?.ClientId || user.id}</span>
          </div>
        </div>
      </div>

      {/* My Bookings Section */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Мои записи</h2>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : clientBookings.filter((booking) => {
            // Фильтруем: показываем только записи на сегодня и будущие
            const bookingDate = parseISO(booking.date);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Сбрасываем время для корректного сравнения дат
            return bookingDate >= today;
          }).length > 0 ? (
          clientBookings
            .filter((booking) => {
              const bookingDate = parseISO(booking.date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return bookingDate >= today;
            })
            .sort((a, b) => {
              // Сортируем по дате (от ближайших к дальним)
              const dateA = parseISO(a.date);
              const dateB = parseISO(b.date);
              return dateA.getTime() - dateB.getTime();
            })
            .map((booking) => {
            const bookingDate = parseISO(booking.date);
            const isPastBooking = bookingDate < new Date();

            return (
              <div key={booking.id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                {/* Header with status */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-3">
                      <h3 className="text-xl font-bold text-gray-900">{booking.serviceName}</h3>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                        {booking.status === 'confirmed' ? '✓ Подтверждена' :
                         booking.status === 'pending' ? '⏳ Ожидает' :
                         booking.status === 'completed' ? '✓ Завершена' : '✕ Отменена'}
                      </span>
                    </div>

                    {/* Booking details */}
                    <div className="space-y-2">
                      <div className="flex items-center text-gray-700">
                        <span className="text-lg mr-2">📅</span>
                        <span className="font-medium">
                          {format(bookingDate, 'EEEE, d MMMM yyyy', { locale: ru })}
                        </span>
                      </div>

                      <div className="flex items-center text-gray-700">
                        <span className="text-lg mr-2">🕐</span>
                        <span className="font-medium text-lg">{booking.time}</span>
                      </div>

                      {booking.employeeName && (
                        <div className="flex items-center text-gray-700">
                          <span className="text-lg mr-2">👤</span>
                          <span><strong>{booking.employeeName}</strong></span>
                        </div>
                      )}

                      {booking.location && (
                        <div className="flex items-center text-gray-700">
                          <span className="text-lg mr-2">📍</span>
                          <span>{booking.location}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Price - показываем только если есть */}
                  {booking.price > 0 && (
                    <div className="text-right ml-4">
                      <div className="text-2xl font-bold text-emerald-600">{booking.price} ₽</div>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                {booking.status === 'confirmed' && !isPastBooking && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <button
                      onClick={() => handleCancelBooking(booking.id, booking.date, booking.time, booking.serviceName, booking.employeeName, booking.isCourtBooking, booking.isGroupBooking)}
                      disabled={isLoading}
                      className="w-full py-3 px-4 bg-red-50 border-2 border-red-200 text-red-700 rounded-xl text-sm font-bold hover:bg-red-100 hover:border-red-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      🗑️ Отменить запись
                    </button>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📅</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Записей пока нет</h3>
            <p className="text-gray-600">Запишитесь на услугу, чтобы увидеть записи здесь</p>
          </div>
        )}
      </div>

      {/* Restaurant Orders Section */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Заказы в ресторане</h2>

        {restaurantOrders.length > 0 ? (
          restaurantOrders.map((order) => {
            const orderDate = parseISO(order.date);
            const deliveryTypeLabel = order.deliveryType === 'beach_club' ? 'Level' : order.deliveryType === 'panorama' ? 'Panorama' : order.deliveryType === 'pickup' ? 'Самовывоз' : 'Доставка';
            const paymentTypeLabel = order.paymentType === 'cash' ? 'Наличными' : 'Картой';

            return (
              <div key={order.orderId} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                {/* Header with order info */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">Заказ #{order.orderId}</h3>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        order.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        order.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.status === 'completed' ? 'Завершен' :
                         order.status === 'cancelled' ? 'Отменен' :
                         order.status === 'confirmed' ? 'Подтвержден' : 'В обработке'}
                      </span>
                    </div>

                    {/* Order details */}
                    <div className="space-y-2">
                      <div className="flex items-center text-gray-700">
                        <span className="text-lg mr-2">📅</span>
                        <span className="font-medium">
                          {format(orderDate, 'EEEE, d MMMM yyyy, HH:mm', { locale: ru })}
                        </span>
                      </div>

                      <div className="flex items-center text-gray-700">
                        <span className="text-lg mr-2">🍽️</span>
                        <span><strong>Тип получения:</strong> {deliveryTypeLabel}</span>
                      </div>

                      <div className="flex items-center text-gray-700">
                        <span className="text-lg mr-2">💳</span>
                        <span><strong>Оплата:</strong> {paymentTypeLabel}</span>
                      </div>

                      {order.bonusUsed > 0 && (
                        <div className="flex items-center text-gray-700">
                          <span className="text-lg mr-2">💰</span>
                          <span><strong>Использовано бонусов:</strong> {order.bonusUsed}₽</span>
                        </div>
                      )}
                    </div>

                    {/* Order items */}
                    <div className="mt-4 space-y-2">
                      <p className="font-semibold text-gray-800">Состав заказа:</p>
                      <div className="space-y-1">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-gray-600">{item.name} × {item.quantity}</span>
                            <span className="text-gray-800 font-medium">{item.price * item.quantity}₽</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Total price */}
                  <div className="text-right ml-4">
                    <div className="text-2xl font-bold text-emerald-600">{order.total} ₽</div>
                    <p className="text-sm text-gray-500">Итого</p>
                  </div>
                </div>

                {/* QR Code */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col items-center">
                  <QRCodeSVG
                    value={JSON.stringify({
                      orderId: order.orderId,
                      date: order.date,
                      client: user?.first_name || user?.firstName
                        ? `${user?.last_name || user?.lastName || ''} ${user?.first_name || user?.firstName || ''}`.trim()
                        : user?.phone || '',
                      phone: user?.phone || '',
                      clientId: user?.ClientId || '',
                      items: order.items.map(i => `${i.name} x${i.quantity}`),
                      total: order.total,
                      bonusUsed: order.bonusUsed,
                      deliveryType: deliveryTypeLabel,
                      paymentType: paymentTypeLabel,
                      status: order.status,
                    })}
                    size={180}
                    level="M"
                    includeMargin={true}
                  />
                  <p className="text-xs text-gray-400 mt-2">Покажите QR-код официанту</p>
                </div>
              </div>
            );
          })
        ) : (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🍽️</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Заказов пока нет</h3>
            <p className="text-gray-600">Закажите блюда в ресторане, чтобы увидеть историю заказов здесь</p>
          </div>
        )}
      </div>

      {/* Logout Button */}
      <div className="pt-6">
        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="w-full bg-red-50 text-red-700 py-4 px-6 rounded-2xl font-medium hover:bg-red-100 transition-colors flex items-center justify-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span>Выйти из аккаунта</span>
        </button>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Выйти из аккаунта?</h3>
            <p className="text-gray-600 mb-6">Вы уверены, что хотите выйти из своего аккаунта?</p>
            <div className="flex space-x-3">
              <button
                onClick={handleLogout}
                className="flex-1 bg-red-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-red-700"
              >
                Выйти
              </button>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-50"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}